import {
    getFloorLayoutsService,
    getSectionsService,
    getSeatsService,
    searchSeatsService,
    getBookingsService,
    createSectionService,
    updateSectionService,
    deleteSectionService,
    createSeatService,
    updateSeatService,
    deleteSeatService,
    createBookingService,
    cancelBookingService,
    assignPermanentSeatService,
    cancelPermanentBookingService,
    cancelRecurringBookingService,
    cancelPermanentSeatBlockService,
    cancelRecurringSeatBlockService,
    cancelSeatBlockService,
    getAllUsersService,
    createRecurringBookingService,
    createPermanentSeatBlockService,
    createRecurringSeatBlockService,
    createSeatBlockService,
    createAdminSingleDayBookingService,
    createAdminDateRangeBookingService,
    getBookingsBySectionService,
    getAllAdminBookingsService,
    getSeatDetailsService,
} from "../services/adminBookings";

// ─── API ↔ Local mapping ──────────────────────────────────────────────────────

const LOCAL_TYPE_TO_API = { rectangle: "Bay", cabin: "Cabin", cubicle: "Cube", conference: "Conference Room" };
const API_TYPE_TO_LOCAL = {
    Bay: "rectangle",
    Cabin: "cabin",
    "Conference Room": "conference",
    Cube: "cubicle",
};
const API_SEAT_STATUS = { Active: "available", Maintenance: "blocked", Inactive: "blocked" };
const WORKSPACE_SYNC_STORAGE_KEY = "tblr-admin-seat-booking-workspace-v2";

const getDisplayUserName = (user = {}, fallback = "Unknown") => {
    if (!user) return fallback;
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return user.name || user.fullName || fullName || fallback;
};

const toSeatBlockDateTime = (date, time) => {
    if (!date) return undefined;
    const safeTime = time || "09:00";
    return `${date}T${safeTime}:00`;
};

const signalWorkspaceSync = () => {
    try {
        localStorage.setItem(WORKSPACE_SYNC_STORAGE_KEY, new Date().toISOString());
    } catch (error) {
        console.log("Unable to signal workspace sync", error);
    }
};

const mapSection = (s) => ({
    id: s._id,
    name: s.name,
    type: API_TYPE_TO_LOCAL[s.type] || "rectangle",
    x: (s.global_position?.col ?? 0) + 1,
    y: (s.global_position?.row ?? 0) + 1,
    width: s.dimensions?.width || 1,
    height: s.dimensions?.height || 1,
    seatRows: s.seat_dimensions?.rows || 2,
    seatColumns: s.seat_dimensions?.columns || 6,
    status: s.status || "Active",
    seats: [],
});

const mapSeat = (s) => ({
    id: s._id,
    label: s.seat_number,
    row: (s.local_position?.row ?? 0) + 1,
    column: (s.local_position?.col ?? 0) + 1,
    type: s.seat_capability?.toLowerCase() || "laptop",
    status: API_SEAT_STATUS[s.status] || "available",
    sectionId: s.section_id?._id || s.section_id,
    permanentUserId: s.permanent_user_id || null,
});

const formatDateWithTimezone = (dateStr, tz = "Asia/Kolkata") => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr.slice(0, 10);
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d);
        let year, month, day;
        parts.forEach(part => {
            if (part.type === 'year') year = part.value;
            if (part.type === 'month') month = part.value;
            if (part.type === 'day') day = part.value;
        });
        return `${year}-${month}-${day}`;
    } catch (e) {
        return dateStr.slice(0, 10);
    }
};

const mapBooking = (b, apiSeats = [], timezone = "Asia/Kolkata") => {
    let sectionId = b.section_id;
    if (!sectionId) {
        const seat = apiSeats.find((s) => (s._id || s.id) === b.seat_id);
        sectionId = seat?.section_id?._id || seat?.section_id || seat?.sectionId;
    }
    return {
        id: b._id,
        seatId: b.seat_id,
        sectionId,
        bookedFor: b.user_id,
        date: formatDateWithTimezone(b.start_time, timezone),
        bookingType: b.type?.toLowerCase() || "one-time",
        status: b.status?.toLowerCase() === "reserved" ? "active" : b.status?.toLowerCase(),
        note: b.note || "",
        createdAt: b.created_at || new Date().toISOString(),
        updatedAt: b.updated_at || new Date().toISOString(),
    };
};

const mapAdminAllBooking = (b, apiSeats = [], timezone = "Asia/Kolkata") => {
    let sectionId = b.section_id;
    if (!sectionId) {
        const seat = apiSeats.find((s) => (s._id || s.id) === b.seat_id);
        sectionId = seat?.section_id?._id || seat?.section_id || seat?.sectionId;
    }
    let seatCapability = b.seat_capability || b.seat?.seat_capability;
    if (!seatCapability && b.seat_id) {
        const seat = apiSeats.find((s) => (s._id || s.id) === b.seat_id);
        seatCapability = seat?.seat_capability || seat?.type;
    }
    const bookingType = b.booking_details?.type?.toLowerCase() || b.booking_type?.toLowerCase() || b.type?.toLowerCase() || "one-time";
    const restrictionType = (b.booking_details?.restriction_type || b.restriction_type || "").toLowerCase();
    const isBlockedSeat = restrictionType === "block" || bookingType === "block" || b.source === "SeatBlock";
    const isPermanent = bookingType === "permanent";
    
    return {
        id: b.booking_id || b._id,
        seatId: b.seat_id,
        sectionId,
        bookedFor: b.user?._id || b.user_id,
        userName: isBlockedSeat ? "Blocked" : getDisplayUserName(b.user, b.user_name || "Unknown"),
        seatLabel: b.seat_number || b.seat?.seat_number || null,
        sectionName: b.section_name || b.sectionName || null,
        date: isPermanent ? "permanent" : formatDateWithTimezone(b.start_time, timezone),
        bookingType,
        restrictionType,
        source: b.source || null,
        seatType: seatCapability || "laptop",
        status: isBlockedSeat
            ? "active"
            : b.status?.toLowerCase() === "reserved" || b.status?.toLowerCase() === "active"
                ? "active"
                : b.status?.toLowerCase(),
        note: b.note || "",
        createdAt: b.created_at || new Date().toISOString(),
    };
};

const DEFAULT_OFFICE = { name: "Toobler HQ", gridColumns: 12, gridRows: 10 };

// ─── Model ────────────────────────────────────────────────────────────────────

export const adminBookings = {
    state: {
        workspace: {
            office: DEFAULT_OFFICE,
            sections: [],
            bookings: [],
            layoutId: null,
            updatedAt: null,
        },
        sectionBookings: [],
        allAdminBookings: [],
        allAdminBookingsPagination: {
            page: 1,
            limit: 10,
            total: null,
            totalPages: null,
            hasNext: false,
        },
        seats: [],
        seatSearchResults: [],
        users: [],
        selectedSeatDetails: null,
        loading: false,
    },

    reducers: {
        setWorkspace: (state, workspace) => ({ ...state, workspace }),
        setSectionBookings: (state, sectionBookings) => ({ ...state, sectionBookings }),
        setAllAdminBookings: (state, allAdminBookings) => ({ ...state, allAdminBookings }),
        setAllAdminBookingsPagination: (state, allAdminBookingsPagination) => ({
            ...state,
            allAdminBookingsPagination,
        }),
        setSeats: (state, seats) => ({ ...state, seats }),
        setSeatSearchResults: (state, seatSearchResults) => ({ ...state, seatSearchResults }),
        setUsers: (state, users) => ({ ...state, users }),
        setSelectedSeatDetails: (state, details) => ({ ...state, selectedSeatDetails: details }),
        setLoading: (state, loading) => ({ ...state, loading }),
    },

    effects: () => ({

        async loadWorkspace() {
            this.setLoading(true);
            try {
                const layoutsRes = await getFloorLayoutsService();
                const layouts = layoutsRes.data?.data || layoutsRes.data || [];

                if (!layouts.length) {
                    this.setWorkspace({ office: DEFAULT_OFFICE, sections: [], bookings: [], layoutId: null, updatedAt: new Date().toISOString() });
                    return;
                }

                const layout = layouts[0];
                const layoutId = layout._id;
                const office = {
                    name: layout.name || DEFAULT_OFFICE.name,
                    gridColumns: layout.grid_max_width || DEFAULT_OFFICE.gridColumns,
                    gridRows: layout.grid_max_height || DEFAULT_OFFICE.gridRows,
                };

                const [sectionsRes, seatsRes, bookingsRes] = await Promise.all([
                    getSectionsService({ layout_id: layoutId }),
                    getSeatsService(),
                    getAllAdminBookingsService({ limit: 100 }),
                ]);

                // GET /sections returns { data: { layout, sections: [] } }
                const sectionsPayload = sectionsRes.data?.data;
                const rawSections = sectionsPayload?.sections || sectionsPayload || sectionsRes.data;
                const apiSections = Array.isArray(rawSections) ? rawSections : [];
                const rawSeats = seatsRes.data?.data || seatsRes.data;
                const apiSeats = Array.isArray(rawSeats) ? rawSeats : [];
                const rawBookings = bookingsRes.data?.data || bookingsRes.data;
                const apiBookings = Array.isArray(rawBookings) ? rawBookings : [];

                const seatsBySection = apiSeats.reduce((acc, seat) => {
                    const mapped = mapSeat(seat);
                    if (!acc[mapped.sectionId]) acc[mapped.sectionId] = [];
                    acc[mapped.sectionId].push(mapped);
                    return acc;
                }, {});

                const sections = apiSections?.map((s) => {
                    const mapped = mapSection(s);
                    mapped.seats = seatsBySection[mapped.id] || [];
                    return mapped;
                });

                const timezone = sectionsPayload?.timezone || sectionsPayload?.layout_id?.timezone || "Asia/Kolkata";
                const bookings = apiBookings.map((b) => mapAdminAllBooking(b, apiSeats, timezone));

                this.setWorkspace({ office, sections, bookings, layoutId, updatedAt: new Date().toISOString() });
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async loadUsers() {
            try {
                const responseData = await getAllUsersService();
                const rawUsers = responseData.data?.input || responseData.data?.data || responseData.data || [];
                const users = rawUsers.map(u => ({
                    ...u,
                    id: u._id || u.id,
                    fullName: u.fullName || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.user_id
                }));
                this.setUsers(users);
                return responseData.data;
            } catch (err) {
                console.log("err", err);
                throw err;
            }
        },

        // ─── Sections ──────────────────────────────────────────────────────

        async createSection(payload, rootState) {
            this.setLoading(true);
            try {
                const layoutId = rootState.adminBookings.workspace?.layoutId;
                if (!layoutId) throw new Error("No floor layout found. Please create a floor layout first.");

                const responseData = await createSectionService({
                    layout_id: layoutId,
                    name: payload.name?.trim(),
                    type: LOCAL_TYPE_TO_API[payload.type] || "Bay",
                    dimensions: { width: Number(payload.width), height: Number(payload.height) },
                    seat_dimensions: {
                        rows: Number(payload.seatRows) || 2,
                        columns: Number(payload.seatColumns) || 6,
                    },
                    global_position: {
                        col: Math.max(0, Number(payload.x) - 1),
                        row: Math.max(0, Number(payload.y) - 1),
                    },
                });

                const created = responseData.data?.data || responseData.data;
                const section = mapSection(created);

                await this.loadWorkspace();
                signalWorkspaceSync();
                return { section };
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async updateSection(payload) {
            this.setLoading(true);
            try {
                await updateSectionService(payload.id, {
                    name: payload.name?.trim(),
                    type: LOCAL_TYPE_TO_API[payload.type] || "Bay",
                    dimensions: {
                        width: Number(payload.width),
                        height: Number(payload.height),
                    },
                    seat_dimensions: {
                        rows: Number(payload.seatRows) || 2,
                        columns: Number(payload.seatColumns) || 6,
                    },
                    global_position: {
                        col: Math.max(0, Number(payload.x) - 1),
                        row: Math.max(0, Number(payload.y) - 1),
                    },
                });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async deleteSection({ sectionId }) {
            this.setLoading(true);
            try {
                await deleteSectionService(sectionId);
                signalWorkspaceSync();
                await this.loadWorkspace();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async updateSectionDisplaySize({ sectionId, width, height }, rootState) {
            this.setLoading(true);
            try {
                const section = rootState.adminBookings.workspace.sections.find(s => s.id === sectionId);
                if (!section) return;

                await updateSectionService(sectionId, {
                    name: section.name,
                    type: LOCAL_TYPE_TO_API[section.type] || "Bay",
                    dimensions: {
                        width: Number(width) || 1,
                        height: Number(height) || 1,
                    },
                    global_position: {
                        col: section.x - 1,
                        row: section.y - 1,
                    },
                });

                signalWorkspaceSync();
                await this.loadWorkspace();
            } catch (err) {
                console.log("updateSectionDisplaySize error", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        // ─── Seats ─────────────────────────────────────────────────────────

        async loadSeats(params) {
            this.setLoading(true);
            try {
                const responseData = await getSeatsService(params);
                const seats = responseData.data?.data || responseData.data || [];
                this.setSeats(seats);
                return responseData.data;
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async createSeat({ sectionId, seatData }) {
            this.setLoading(true);
            try {
                await createSeatService({
                    section_id: sectionId,
                    seat_number: seatData.label?.trim(),
                    seat_capability: seatData.type === "monitor" ? "Monitor" : seatData.type === "desktop" ? "Desktop" : "Laptop",
                    local_position: {
                        col: Math.max(0, Number(seatData.column) - 1),
                        row: Math.max(0, Number(seatData.row) - 1),
                    },
                });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async updateSeat({ id, seatData }) {
            this.setLoading(true);
            try {
                const payload = {};
                if (seatData.label !== undefined) {
                    payload.seat_number = seatData.label?.trim();
                }
                if (seatData.type !== undefined) {
                    payload.seat_capability = seatData.type === "monitor" ? "Monitor" : seatData.type === "desktop" ? "Desktop" : "Laptop";
                }
                if (seatData.status !== undefined) {
                    const statusMap = { available: "Active", blocked: "Maintenance" };
                    payload.status = statusMap[seatData.status] || "Active";
                }
                if (seatData.row !== undefined || seatData.column !== undefined) {
                    payload.local_position = {
                        col: Math.max(0, Number(seatData.column) - 1),
                        row: Math.max(0, Number(seatData.row) - 1),
                    };
                }
                await updateSeatService(id, payload);
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async deleteSeat({ seatId }) {
            this.setLoading(true);
            try {
                await deleteSeatService(seatId);
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async searchSeats({ layoutId, startTime, endTime, seatCapability }) {
            this.setLoading(true);
            try {
                const params = {
                    layout_id: layoutId,
                    start_time: startTime,
                    end_time: endTime,
                };
                if (seatCapability) params.seat_capability = seatCapability;
                const responseData = await searchSeatsService(params);
                const results = responseData.data?.data || responseData.data || [];
                this.setSeatSearchResults(results);
                return results;
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        // ─── Bookings ──────────────────────────────────────────────────────

        async createMultipleBookings(payload) {
            const { dates, seatId, bookedFor, bookingType, startDate, endDate, weekdays, startTime, endTime } = payload;
            if (!bookedFor && !payload.isBlockedSeat) {
                throw new Error("Please select a user to book the seat for.");
            }
            this.setLoading(true);
            try {
                const isBlockedSeat = Boolean(payload.isBlockedSeat);
                const blockBasePayload = {
                    seat_id: seatId,
                    restriction_type: "Block",
                    ...(payload.note?.trim() ? { reason: payload.note.trim() } : {}),
                };

                if (bookingType === "permanent") {
                    if (isBlockedSeat) {
                        await createPermanentSeatBlockService({
                            ...blockBasePayload,
                            start_date: startDate,
                        });
                    } else {
                        await assignPermanentSeatService({ seat_id: seatId, user_id: bookedFor });
                    }
                } else if (bookingType === "recurring") {
                    if (isBlockedSeat) {
                        await createRecurringSeatBlockService({
                            ...blockBasePayload,
                            recurrence_days: weekdays,
                            recurrence_type: "weekly",
                            start_date: startDate,
                        });
                    } else {
                        await createRecurringBookingService({
                            seat_id: seatId,
                            user_id: bookedFor,
                            recurrence_days: weekdays,
                            recurrence_type: "weekly",
                            start_date: startDate,
                        });
                    }
                } else if (bookingType === "date-range") {
                    if (isBlockedSeat) {
                        await createSeatBlockService({
                            ...blockBasePayload,
                            start_time: toSeatBlockDateTime(startDate, startTime),
                            end_time: toSeatBlockDateTime(endDate, endTime),
                            type: "Date-Range",
                        });
                    } else {
                        await createAdminDateRangeBookingService({
                            seat_id: seatId,
                            user_id: bookedFor,
                            start_date: startDate,
                            end_date: endDate,
                        });
                    }
                } else {
                    for (const date of dates) {
                        if (isBlockedSeat) {
                            await createSeatBlockService({
                                ...blockBasePayload,
                                start_time: toSeatBlockDateTime(date, startTime),
                                end_time: toSeatBlockDateTime(date, endTime),
                                type: "One-time",
                            });
                        } else {
                            await createAdminSingleDayBookingService({
                                seat_id: seatId,
                                user_id: bookedFor,
                                date: date,
                                start_time: startTime || "09:00",
                                end_time: endTime || "18:00",
                            });
                        }
                    }
                }
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelBooking({ bookingId, reason }) {
            this.setLoading(true);
            try {
                await cancelBookingService(bookingId, { reason: reason || "Cancelled by admin" });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async loadSectionBookings({ sectionId, date }, rootState) {
            if (!sectionId) return [];
            this.setLoading(true);
            try {
                this.setSectionBookings([]);
                const responseData = await getBookingsBySectionService(sectionId, { date });
                // GET /bookings/section/{id} returns { section: {}, bookings: [] }
                const rawData = responseData.data?.data?.bookings || responseData.data?.bookings || responseData.data?.data || [];
                const apiBookings = Array.isArray(rawData) ? rawData : [];

                const bookings = apiBookings.map(b => ({
                    id: b.booking_id || b._id,
                    seatId: b.seat?._id || b.seat_id?._id || b.seat_id,
                    sectionId: sectionId,
                    bookedFor: b.user?._id || b.user_id?._id || b.user_id?.id || b.user_id,
                    userName: (
                        (b.booking_details?.restriction_type || b.restriction_type || "").toLowerCase() === "block" ||
                        (b.source || "").toLowerCase() === "seatblock" ||
                        (!b.user && !b.user_id && (b.user_name === "N/A" || b.username === "N/A"))
                    )
                        ? "Blocked"
                        : getDisplayUserName(b.user || b.user_id, b.user_name || "Unknown"),
                    date: formatDateWithTimezone(b.start_time) || null,
                    seatLabel: b.seat?.seat_number || b.seat_number || null,
                    bookingType: b.booking_details?.type?.toLowerCase() || b.booking_type?.toLowerCase() || b.type?.toLowerCase() || "one-time",
                    restrictionType: b.booking_details?.restriction_type || b.restriction_type || null,
                    source: b.source || null,
                    seatType: b.seat?.seat_capability || b.seat_capability || "laptop",
                    status: b.status?.toLowerCase() === "reserved" ? "active" : (b.status?.toLowerCase() || "active"),
                    note: b.note || "",
                    startTime: b.start_time,
                    endTime: b.end_time,
                    createdAt: b.created_at || new Date().toISOString(),
                }));

                this.setSectionBookings(bookings);
                return bookings;
            } catch (err) {
                console.log("loadSectionBookings error", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async loadAllAdminBookings(params = {}, rootState) {
            this.setLoading(true);
            try {
                // Pass page and limit directly to the backend for server-side pagination
                const query = { limit: 10, page: 1, ...params };
                const responseData = await getAllAdminBookingsService(query);
                const rawData = responseData.data?.data || responseData.data || [];
                const apiBookings = Array.isArray(rawData) ? rawData : [];
                const meta =
                    responseData.data?.pagination ||
                    responseData.data?.meta ||
                    responseData.data?.page_info ||
                    {};
                
                const currentWorkspace = rootState.adminBookings.workspace;
                const apiSeats = rootState.adminBookings.seats || [];
                
                // Map the bookings from the current page
                const bookings = apiBookings.map(b => mapAdminAllBooking(b, apiSeats));

                const currentPage = Number(meta.page || meta.currentPage || query.page || 1);
                const currentLimit = Number(meta.limit || meta.per_page || query.limit || 10);
                const total = (meta.total !== undefined && meta.total !== null) ? Number(meta.total) : (meta.total_count !== undefined ? Number(meta.total_count) : null);
                
                const totalPages =
                    meta.totalPages ??
                    meta.total_pages ??
                    (total !== null && currentLimit ? Math.ceil(total / currentLimit) : 0);

                const hasNext =
                    typeof meta.hasNext === "boolean"
                        ? meta.hasNext
                        : typeof meta.has_next === "boolean"
                            ? meta.has_next
                            : totalPages
                                ? currentPage < totalPages
                                : apiBookings.length === currentLimit;

                this.setAllAdminBookings(bookings);
                this.setAllAdminBookingsPagination({
                    page: currentPage,
                    limit: currentLimit,
                    total,
                    totalPages,
                    hasNext,
                });
                this.setWorkspace({
                    ...currentWorkspace,
                    updatedAt: new Date().toISOString(),
                });
                return bookings;
            } catch (err) {
                console.log("loadAllAdminBookings error", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async assignPermanentSeat({ seatId, userId }) {
            this.setLoading(true);
            try {
                await assignPermanentSeatService({ seat_id: seatId, user_id: userId });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelPermanentBooking(id) {
            this.setLoading(true);
            try {
                await cancelPermanentBookingService(id);
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelRecurringBooking(id) {
            this.setLoading(true);
            try {
                await cancelRecurringBookingService(id);
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelSeatBlock({ blockId, reason }) {
            this.setLoading(true);
            try {
                await cancelSeatBlockService(blockId, {
                    cancellation_reason: reason || "Cancelled by admin",
                });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelRecurringSeatBlock({ blockId, reason }) {
            this.setLoading(true);
            try {
                await cancelRecurringSeatBlockService(blockId, {
                    cancellation_reason: reason || "Cancelled by admin",
                });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelPermanentSeatBlock({ blockId, reason }) {
            this.setLoading(true);
            try {
                await cancelPermanentSeatBlockService(blockId, {
                    cancellation_reason: reason || "Cancelled by admin",
                });
                await this.loadWorkspace();
                signalWorkspaceSync();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async loadSeatDetails(seatId) {
            this.setLoading(true);
            try {
                this.setSelectedSeatDetails(null);
                const responseData = await getSeatDetailsService(seatId);
                const body = responseData.data;
                
                // Handle different response structures
                let details = null;
                if (body && Array.isArray(body.data)) {
                    details = body.data[0];
                } else if (body && body.data) {
                    details = body.data;
                } else if (Array.isArray(body)) {
                    details = body[0];
                } else {
                    details = body;
                }
                
                this.setSelectedSeatDetails(details);
                return details;
            } catch (err) {
                console.log("loadSeatDetails error", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
    }),
};
